// task-row.js — CLIENT-SIDE shaping, raw FHIR Task -> the shapeTask DTO
// (server/opd/tasks.mjs:337-358) the Tasks screen already renders. This is the
// FHIR-direct port of the server's shapeTask + dedupeTasks: same fields, same
// dedupe key, computed in the browser off the Task resource the doctor's own
// token read (RLS-scoped) instead of the disk-first `_healther` blob.
//
// Two provenance shapes must both round-trip through this:
//   • APP-created Tasks carry the internal `_healther` bag as a FHIR extension
//     (url http://healther.io/fhir/StructureDefinition/opd-healther, valueString =
//     JSON.stringify(_healther)) — the same extension medplum-store.mjs's
//     encodeHealtherExtension writes. We decode it and prefer it (it holds
//     ownerId/performerRole/taskType/dept/createdBy exactly as the server stored).
//   • BOT-born Tasks (a Medplum Bot wrote them directly in the cloud) have NO
//     `_healther` extension. For those we DERIVE the same fields from pure FHIR —
//     the port of fhirTaskToHealther (tasks.mjs:148-160): taskType from
//     code.coding[system=task-type], ownerId from owner.reference, performerRole
//     from performerType[0].text.
// Decoding-then-deriving means one shaper handles both without the caller caring.

const EXT_OPD_HEALTHER = 'http://healther.io/fhir/StructureDefinition/opd-healther';
const EXT_OPD_ENCOUNTER_REF = 'https://healther.io/opd/encounter-ref'; // where _encounterId rides on the wire
const EXT_OPD_ACCESS_GRANT = 'https://healther.io/opd/access-grant'; // access-grant Bot's payload (JSON valueString)
const TASK_TYPE_SYSTEM = 'https://healther.io/opd/task-type';
const OPD_SLUG_RE = /healther\.io\/opd\//; // identifier system marking an OPD slug

// Human labels for the known task types — mirrors TASK_TYPES (tasks.mjs:64-73).
// Only used to fill a description when the Task carries neither description nor a
// derivable one; the screen otherwise reads Task.description verbatim.
const TASK_TYPE_LABELS = {
  'review-report': 'Review result / report',
  'follow-up-call': 'Call patient / follow-up',
  'verify-id': 'Verify patient ID',
  'sign-off': 'Senior sign-off needed',
  'collect-sample': 'Collect lab sample',
  'chase-result': 'Chase pending result',
  'kiosk-routing-handoff': 'Kiosk routing handoff',
  'scheduling-help': 'Scheduling help',
  refill: 'Prescription refill',
  general: 'General task',
};

// ── Worklist category axis (Tasks.jsx filter + grouping) ─────────────────────
// Two buckets over taskType:
//   identity — front-desk / identity / admin / messaging / scheduling ops
//   clinical — needs clinical judgment (results, meds, labs, sign-off, care plans)
//
// Explicit mapping (mirrors TYPE_LABEL + bot/automation vocabulary):
//   identity: verify-id, follow-up-call, follow-up, respond-to-message,
//             access-granted, callback, general
//   clinical: review-report, sign-off, review-conflicts, collect-sample,
//             chase-result, refill, care-plan-step
// Unknown types: if the code looks patient-care/clinical → clinical; else identity.
export const TASK_CATEGORY = { IDENTITY: 'identity', CLINICAL: 'clinical' };
export const TASK_CATEGORY_LABEL = {
  identity: 'Identity & Admin',
  clinical: 'Clinical',
};
const IDENTITY_ADMIN_TASK_TYPES = new Set([
  'verify-id', 'follow-up-call', 'follow-up', 'respond-to-message',
  'access-granted', 'callback', 'kiosk-routing-handoff', 'scheduling-help', 'general',
]);
const CLINICAL_TASK_TYPES = new Set([
  'review-report', 'sign-off', 'review-conflicts', 'collect-sample',
  'chase-result', 'refill', 'care-plan-step',
]);
const CLINICAL_TYPE_HEURISTIC = /review|sign|conflict|lab|sample|result|refill|prescri|med|diag|report|care-plan|clinical/i;

export function taskCategoryOf(taskType) {
  const t = String(taskType || 'general').trim().toLowerCase();
  if (IDENTITY_ADMIN_TASK_TYPES.has(t)) return TASK_CATEGORY.IDENTITY;
  if (CLINICAL_TASK_TYPES.has(t)) return TASK_CATEGORY.CLINICAL;
  if (CLINICAL_TYPE_HEURISTIC.test(t)) return TASK_CATEGORY.CLINICAL;
  return TASK_CATEGORY.IDENTITY;
}

// Decode the opd-healther extension blob back into a `_healther`-shaped object, or
// null if the Task has none (a Bot-born Task). Inverse of encodeHealtherExtension.
function decodeHealther(task) {
  const ext = (task && Array.isArray(task.extension) ? task.extension : [])
    .find((e) => e && e.url === EXT_OPD_HEALTHER && e.valueString);
  if (!ext) return null;
  try { return JSON.parse(ext.valueString); } catch (_) { return null; }
}

// The access-grant Bot's payload {granteeRef, granteeDisplay, granteeDept, mode,
// patientId} riding EXT_OPD_ACCESS_GRANT as JSON.stringify'd valueString (THE BOT
// CONTRACT). null when absent or unparseable — the Tasks screen's AccessGrantedPanel
// surfaces the miss loudly (revoke disabled + visible notice) instead of guessing.
function decodeAccessGrant(task) {
  const ext = (task && Array.isArray(task.extension) ? task.extension : [])
    .find((e) => e && e.url === EXT_OPD_ACCESS_GRANT && e.valueString);
  if (!ext) return null;
  try { return JSON.parse(ext.valueString); } catch (_) { return null; }
}

// The encounter id for a Task. App writes it to _encounterId (a disk-only field that
// rides an EXT_OPD_ENCOUNTER_REF extension on the wire); FHIR-correct Tasks carry it
// on Task.encounter.reference. We read the extension first, then fall back to the
// standard field so both shapes work.
function encounterIdOf(task) {
  const encExt = (task && Array.isArray(task.extension) ? task.extension : [])
    .find((e) => e && e.url === EXT_OPD_ENCOUNTER_REF && e.valueReference);
  const ref = (encExt && encExt.valueReference && encExt.valueReference.reference)
    || (task && task.encounter && task.encounter.reference)
    || '';
  return ref.startsWith('Encounter/') ? ref.split('/')[1] : (ref || null);
}

function typeCoding(task) {
  const codings = (task && task.code && Array.isArray(task.code.coding)) ? task.code.coding : [];
  return codings.find((c) => c && c.system === TASK_TYPE_SYSTEM) || null;
}

// basedOn is FHIR-shaped as an array; surface the first reference — the request that
// spawned this task (mirrors firstBasedOnRef, tasks.mjs:333-336).
function firstBasedOnRef(task) {
  const list = Array.isArray(task.basedOn) ? task.basedOn : (task.basedOn ? [task.basedOn] : []);
  return (list[0] && list[0].reference) || null;
}

// Raw FHIR Task -> the exact DTO Tasks.jsx renders (shapeTask, tasks.mjs:337-358).
// `healther` is the decoded/derived `_healther`; owner.id/performerRole/taskType
// come from it so app- and bot-born Tasks read identically.
export function shapeTask(task) {
  const healther = decodeHealther(task) || {};
  const derivedTaskType = (typeCoding(task) && typeCoding(task).code)
    || (task.code && task.code.text) || 'general';
  const taskType = healther.taskType || derivedTaskType;
  const ownerRef = String((task.owner && task.owner.reference) || '');
  const ownerId = healther.ownerId
    || (ownerRef.startsWith('Practitioner/') ? ownerRef.split('/')[1] : null);
  const performerRole = (healther.performerRole !== undefined && healther.performerRole !== null)
    ? healther.performerRole
    : ((task.performerType && task.performerType[0] && task.performerType[0].text) || null);
  const createdBy = healther.createdBy || null;

  return {
    id: task.id,
    taskType,
    description: task.description || TASK_TYPE_LABELS[taskType] || '',
    status: task.status,
    priority: task.priority || 'routine',
    businessStatus: (task.businessStatus && task.businessStatus.text) || null,
    patient: task.for
      ? { id: (String(task.for.reference || '').split('/')[1]) || null, name: task.for.display || null }
      : null,
    owner: task.owner ? { id: ownerId, name: task.owner.display || null } : null,
    // Who asked for the work. App Tasks store requester.display; bot Tasks may have
    // none → fall back to the createdBy marker so the card always names a source.
    requester: (task.requester && task.requester.display) || (createdBy === 'bot' ? 'Automation' : null),
    performerRole,
    focus: (task.focus && task.focus.reference) || null,
    basedOn: firstBasedOnRef(task),
    encounterId: encounterIdOf(task),
    due: (task.restriction && task.restriction.period && task.restriction.period.end) || null,
    authoredOn: task.authoredOn || null,
    notes: (task.note || []).map((n) => ({ text: n.text, time: n.time || null, author: n.authorString || null })),
    // access-granted Tasks (the access-grant Bot) carry their grant payload in a
    // dedicated extension — surfaced so the panel can one-tap revoke without re-fetching.
    accessGrant: decodeAccessGrant(task),
  };
}

// ── De-dupe (port of dedupeTasks, tasks.mjs:284-307) ──────────────────────────
// Two ROW kinds repeat: a local-slug row + its Medplum-UUID mirror, and Bot-born
// Tasks the same automation created more than once for the SAME work. Collapse on a
// stable IDENTITY key (slug → focus ref → type+patient+description) keeping the
// most-progressed / newest row. This is the exact server algorithm, run client-side
// over the raw Task list BEFORE shaping.
const STATUS_RANK = { completed: 0, cancelled: 1, 'in-progress': 2, 'on-hold': 3, ready: 4, requested: 5 };
const TERMINAL_STATUS = new Set(['completed', 'cancelled']);

function preferTaskRow(next, prev) {
  const nextTerm = TERMINAL_STATUS.has(next.status);
  const prevTerm = TERMINAL_STATUS.has(prev.status);
  // An open verify-id must not lose to a stale completed row with the same key.
  if (nextTerm !== prevTerm) return nextTerm ? prev : next;
  const better = (STATUS_RANK[next.status] ?? 9) < (STATUS_RANK[prev.status] ?? 9)
    || ((STATUS_RANK[next.status] ?? 9) === (STATUS_RANK[prev.status] ?? 9)
      && String(next.authoredOn || '') > String(prev.authoredOn || ''));
  return better ? next : prev;
}

function identityKey(t) {
  const healther = decodeHealther(t) || {};
  const slug = healther.slug || t._opdSlug
    || ((t.identifier || []).find((i) => OPD_SLUG_RE.test(String((i && i.system) || ''))) || {}).value;
  if (slug) return `slug:${slug}`;
  const patientRef = (t.for && t.for.reference) || '';
  const focus = t.focus && t.focus.reference;
  // verify-id duplicate Tasks share focus (the dup-candidate Patient) across many
  // distinct new registrations — pair focus with for so each subject survives.
  if (focus) return `focus:${focus}|${patientRef}`;
  const type = healther.taskType || (t.code && t.code.text) || 'general';
  return `tpd:${type}|${patientRef}|${String(t.description || '').trim().toLowerCase()}`;
}

// Collapse duplicate raw Tasks. Input + output are RAW FHIR Tasks (shape after).
export function dedupeTasks(rows) {
  const byKey = new Map();
  for (const t of rows) {
    const k = identityKey(t);
    const prev = byKey.get(k);
    if (!prev) { byKey.set(k, t); continue; }
    byKey.set(k, preferTaskRow(t, prev));
  }
  return [...byKey.values()];
}
